import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ErrorReportService } from '../../core-module/services/error-report.service';
import { IErrorInfo } from '../../shared-module/models/error-info';
import { AccountService } from '../../core-module/services/account.service';
import { UserProfileService } from '../../core-module/services/user-profile.service';
import { AuthService } from '../../shared-module/services/auth.service';
import { RoutingService } from '../../shared-module/services/routing.service';
import { takeUntil, finalize } from 'rxjs/operators';
import { ObservableBaseComponent } from '../../shared-module/components/observable-base.component';

@Component({
  selector: 'app-error',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule
  ],
  template: `
    <div class="row mt-5">
      <div class="mx-auto">
        <div class="error-card">
          <div class="card-header">
            <div class="error-icon">
              <i class="material-icons">error_outline</i>
            </div>
            <h1>Something Went Wrong</h1>
            <p>We encountered an unexpected error</p>
          </div>

          <div class="card-content">
            <div class="friendly-message">
              We've captured the error details below. Enter your email address and click <strong>Send Error Report</strong> — our support team will receive the full details and follow up with you as soon as possible.
            </div>

            @if (errorInfo()) {
              <div class="error-details">
                <h3>
                  <i class="material-icons">info_outline</i>
                  Error Details
                </h3>

                <div class="detail-row">
                  <span class="detail-label">Error ID:</span>
                  <span class="detail-value">{{ errorInfo()!.errorId || 'N/A' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Time:</span>
                  <span class="detail-value">{{ errorInfo()!.timestamp | date:'medium' }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">Page:</span>
                  <span class="detail-value url-value">{{ errorInfo()!.url || 'N/A' }}</span>
                </div>

                <div class="detail-section">
                  <div class="detail-label">Message:</div>
                  <div class="detail-value message-value">{{ errorInfo()!.message }}</div>
                </div>

                @if (errorInfo()!.details) {
                  <div class="detail-section">
                    <div class="detail-label">Technical Details:</div>
                    <div class="detail-value technical-details">{{ errorInfo()!.details }}</div>
                  </div>
                }
              </div>
            }

            <div class="email-input-section">
              <mat-form-field appearance="outline" class="email-field">
                <mat-label>Your Email</mat-label>
                <input matInput type="email" placeholder="Enter your email to send the report"
                  [value]="emailInput()"
                  (input)="emailInput.set($any($event.target).value)" />
                <mat-hint>We'll use this to follow up on your error report</mat-hint>
              </mat-form-field>
            </div>

            <div class="action-buttons">
              <button 
                mat-raised-button
                color="primary"
                class="primary-button" 
                (click)="sendErrorEmail()"
                [disabled]="isSending() || emailSent() || !emailInput()">
                <i class="material-icons">{{ emailSent() ? 'check_circle' : 'email' }}</i>
                {{ emailSent() ? 'Email Sent!' : (isSending() ? 'Sending...' : 'Send Error Report via Email') }}
              </button>

              <a mat-raised-button class="tertiary-button" [routerLink]="['/dashboard']">
                <i class="material-icons">home</i>
                Go to Dashboard
              </a>

              <button 
                mat-raised-button
                class="logout-button"
                (click)="logout()">
                <i class="material-icons">login</i>
                Go to Login Page
              </button>
            </div>

            @if (emailSent()) {
              <div class="success-message">
                <i class="material-icons">check_circle_outline</i>
                Your error report has been sent to our support team. We'll look into it as soon as possible.
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .error-card {
      background: white;
      border-radius: 16px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      padding: 0;
      max-width: 800px;
      width: 100%;
      overflow: hidden;
      text-align: center;
      animation: slideUp 0.6s ease-out;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(40px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .card-header {
      background: linear-gradient(135deg, #ff9966 0%, #ff5e62 100%);
      padding: 32px;
      position: relative;
      overflow: hidden;
    }

    .card-header::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
      pointer-events: none;
    }

    .error-icon {
      width: 80px;
      height: 80px;
      margin: 0 auto 16px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 40px;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }

    .error-icon .material-icons {
      font-size: 48px;
    }

    .card-header h1 {
      color: white;
      font-size: 28px;
      font-weight: 700;
      margin: 0 0 8px 0;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .card-header p {
      color: rgba(255, 255, 255, 0.9);
      font-size: 16px;
      margin: 0;
    }

    .card-content {
      padding: 32px;
    }

    .friendly-message {
      font-size: 16px;
      color: #555;
      line-height: 1.6;
      margin-bottom: 24px;
      text-align: left;
    }

    .error-details {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 24px;
      text-align: left;
      border-left: 4px solid #ff9966;
    }

    .error-details h3 {
      color: #333;
      font-size: 18px;
      margin: 0 0 16px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .error-details h3 .material-icons {
      font-size: 20px;
      color: #ff9966;
    }

    .detail-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #e9ecef;
      gap: 16px;
    }

    .detail-row:last-child {
      border-bottom: none;
    }

    .detail-section {
      padding: 12px 0;
      border-bottom: 1px solid #e9ecef;
    }

    .detail-section:last-child {
      border-bottom: none;
    }

    .detail-label {
      font-weight: 600;
      color: #6c757d;
      font-size: 14px;
      flex-shrink: 0;
    }

    .detail-value {
      color: #333;
      font-size: 14px;
      word-break: break-word;
    }

    .url-value {
      font-family: 'Courier New', monospace;
      font-size: 12px;
      color: #007bff;
    }

    .message-value {
      margin-top: 8px;
      padding: 12px;
      background: white;
      border-radius: 4px;
      border-left: 3px solid #ff9966;
      font-size: 14px;
    }

    .technical-details {
      margin-top: 8px;
      padding: 12px;
      background: #2d2d2d;
      color: #f8f9fa;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      white-space: pre-wrap;
      word-break: break-all;
      max-height: 200px;
      overflow-y: auto;
    }

    .email-input-section {
      margin-bottom: 20px;
      text-align: left;
    }

    .email-field {
      width: 100%;
    }

    .action-buttons {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-bottom: 16px;
    }

    .primary-button,
    .tertiary-button {
      padding: 14px 24px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      text-decoration: none;
      border: none;
      width: 100%;
    }

    .primary-button {
      background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
      color: white;
      box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
    }

    .primary-button:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(25, 118, 210, 0.4);
    }

    .primary-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .primary-button .material-icons {
      font-size: 20px;
    }

    .tertiary-button {
      background: #6c757d;
      color: white;
    }

    .tertiary-button:hover {
      background: #5a6268;
      transform: translateY(-2px);
    }

    .tertiary-button .material-icons {
      font-size: 20px;
    }

    .logout-button {
      background: #f8f9fa;
      color: #6c757d;
      border: 1px solid #dee2e6;
    }

    .logout-button:hover {
      background: #e9ecef;
      transform: translateY(-2px);
    }

    .logout-button .material-icons {
      font-size: 20px;
    }

    .success-message {
      background: #d4edda;
      color: #155724;
      padding: 16px;
      border-radius: 8px;
      border-left: 4px solid #28a745;
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 14px;
      animation: fadeIn 0.3s ease;
    }

    .success-message .material-icons {
      font-size: 24px;
      color: #28a745;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }

    @media (max-width: 768px) {
      .error-card {
        margin: 16px;
      }

      .card-header {
        padding: 24px 16px;
      }

      .card-content {
        padding: 24px 16px;
      }

      .action-buttons {
        flex-direction: column;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ErrorComponent extends ObservableBaseComponent implements OnInit {
  errorInfo = signal<IErrorInfo | null>(null);
  emailInput = signal('');
  isSending = signal(false);
  emailSent = signal(false);

  constructor(
    private errorReportService: ErrorReportService,
    private accountService: AccountService,
    private userProfileService: UserProfileService,
    private authService: AuthService,
    private routingService: RoutingService
  ) {
    super();
  }

  ngOnInit(): void {
    const error = this.errorReportService.getError();
    
    if (error && this.accountService.currentUser) {
      error.userId = this.accountService.currentUser.id?.toString();
      const fullName = `${this.accountService.currentUser.firstName} ${this.accountService.currentUser.lastName}`.trim();
      error.userName = fullName || this.accountService.currentUser.email || undefined;
      error.userEmail = this.accountService.currentUser.email || undefined;
    }

    // If no error info, redirect to home
    if (!error) {
      window.location.href = '/';
      return;
    }

    this.errorInfo.set(error);
    this.emailInput.set(error?.userEmail ?? '');
  }


  sendErrorEmail(): void {
    const error = this.errorInfo();
    if (!error || this.isSending()) return;

    const email = this.emailInput().trim();
    if (!email) return;
    error.userEmail = email;

    this.isSending.set(true);

    this.errorReportService.sendErrorReport(error)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.isSending.set(false);
        })
      )
      .subscribe({
        next: () => {
          this.emailSent.set(true);
        },
        error: (err) => {
          console.error('Failed to send error report:', err);
          alert('Failed to send error report. Please try again or contact support directly.');
        }
      });
  }

  logout(): void {
    this.accountService.currentUser = null;
    this.userProfileService.removeUserProfile();
    this.authService.clearToken();
    this.routingService.navigate(['login']);
  }

}
